import { Reveal } from "@/components/site/Reveal";
import Photo from "@/components/site/Photo";
import PhoneMockup from "@/components/site/PhoneMockup";

// Editorial composition, strictly contained inside the visual column.
// Stacking order (single shared component — every instance inherits it):
//   1. lifestyle photo (z-0, behind)
//   2. phone mockup (z-10, clearly in front, overlapping the photo's left edge)
//   3. complete app screenshot at its original aspect ratio
//   4. phone bezel with separate hardware space outside the screenshot (PhoneMockup)
// The phone is prominent (~70-80% of the photo height on desktop) and never
// crosses into the text column. Mobile: photo, then phone centred below.
export const PhonePhotoCollage = ({ phoneSrc, phoneAlt, photo, priority = false, testid }) => (
  <div data-testid={testid}>
    <div className="hidden items-center sm:flex sm:pl-4">
      <div className="relative z-10 w-52 flex-none lg:w-64">
        <PhoneMockup src={phoneSrc} alt={phoneAlt} className="!max-w-none" priority={priority} />
      </div>
      <div className="relative z-0 -ml-10 min-w-0 flex-1 md:-ml-12">
        <Photo slug={photo} ratio="aspect-[4/3]" priority={priority} className="shadow-card" />
      </div>
    </div>
    <div className="sm:hidden">
      <Photo slug={photo} ratio="aspect-[4/3]" priority={priority} className="shadow-card" />
      <div className="mt-8 flex justify-center">
        <PhoneMockup src={phoneSrc} alt={phoneAlt} className="!max-w-[320px]" />
      </div>
    </div>
  </div>
);

// Full split hero section: copy column (45%) + visual column (55%),
// both inside the global container with a generous, consistent gap.
export const ProductLifestyleSplit = ({
  eyebrow,
  eyebrowClass = "bg-teal/10 text-teal",
  title,
  body,
  children,
  phoneSrc,
  phoneAlt,
  photo,
  priority = false,
  reverse = false,
  tone = "light",
  testid,
}) => (
  <section className={tone === "white" ? "bg-white" : "bg-lightbg"} data-testid={testid}>
    <div className="container-x grid grid-cols-1 items-center gap-8 section-space lg:grid-cols-[45fr_55fr] lg:gap-12">
      <Reveal className={`max-w-xl ${reverse ? "lg:order-2" : ""}`}>
        {eyebrow && (
          <span className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider ${eyebrowClass}`}>
            {eyebrow}
          </span>
        )}
        <h1 className="mt-4 font-heading text-4xl font-bold tracking-tight text-navy md:text-5xl">{title}</h1>
        <p className="mt-5 text-lg text-bodygrey">{body}</p>
        {children && <div className="mt-8 flex flex-wrap items-center gap-3">{children}</div>}
      </Reveal>
      <Reveal delay={0.1} className={reverse ? "lg:order-1" : ""}>
        <PhonePhotoCollage
          phoneSrc={phoneSrc}
          phoneAlt={phoneAlt}
          photo={photo}
          priority={priority}
          testid={testid ? `${testid}-visual` : undefined}
        />
      </Reveal>
    </div>
  </section>
);

export default ProductLifestyleSplit;

